import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '@/config/api';
import {
  openAudioQueue, enqueueAudio, listPendingAudio,
  removePendingAudio, uploadAudioSegment,
} from './audioUploadQueue.js';

// Never discard a buffered voice chunk merely because the network dropped.
// Permanent errors are visible to the conductor, and stay in IndexedDB
// until the issue is resolved or the user explicitly deletes the recording.
const discardCodes = new Set([
  'AUDIO_VOICE_CONSENT_CHANGED', 'AUDIO_SEGMENT_REVOKED',
]);
const fatalCaptureCodes = new Set([
  'CAPTURE_ALREADY_IN_ANOTHER_TAB',
  'AUDIO_CAPTURE_NOT_AUTHORIZED',
  'RECORDING_NOT_ACTIVE',
  'AUDIO_QUOTA_EXCEEDED',
  'AUDIO_QUEUE_FULL',
]);

const captureKey = (recordingId) => 'leela-audio-capture-' + recordingId;

export default function useAudioUploadSession({
  gameId, token, recording, connected, conductor,
}) {
  const [state, setState] = useState('idle');
  const [lastError, setLastError] = useState('');
  const dbRef = useRef(null);
  const openingRef = useRef(null);
  const captureRef = useRef(null);
  const flushingRef = useRef(false);
  const activeRef = useRef(false);
  const renewingRef = useRef(false);
  // A successful heartbeat only proves that the capture lease is alive.
  // It must never erase an upload failure or show a false "ready" status.
  const uploadBlockedRef = useRef(false);

  const ensureQueue = useCallback(() => {
    if (dbRef.current) return Promise.resolve(dbRef.current);
    if (!openingRef.current) {
      openingRef.current = openAudioQueue().then((db) => {
        dbRef.current = db;
        return db;
      }).finally(() => { openingRef.current = null; });
    }
    return openingRef.current;
  }, []);

  const postJson = useCallback(async (action, payload) => {
    const response = await fetch(apiUrl('/api/games/' + gameId + '/recording/audio/' + action), {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const error = new Error(data?.code || 'AUDIO_API_' + response.status);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }, [gameId, token]);

  const flush = useCallback(async () => {
    if (flushingRef.current || !dbRef.current || !token || !gameId) return;
    flushingRef.current = true;
    try {
      // Includes segments from completed sessions, which can be delivered
      // after the facilitator reconnects and the recording has stopped.
      const queue = await listPendingAudio(dbRef.current);
      for (const entry of queue) {
        try {
          await uploadAudioSegment({
            entry, gameId: entry.gameId || gameId, token, apiUrl,
          });
          await removePendingAudio(dbRef.current, entry.uploadId);
          uploadBlockedRef.current = false;
          setLastError('');
          if (activeRef.current) setState('ready');
        } catch (error) {
          const code = error.code || error.message || 'AUDIO_UPLOAD_FAILED';
          if (discardCodes.has(code)) {
            // Never retry audio which may include a revoked participant.
            await removePendingAudio(dbRef.current, entry.uploadId);
            continue;
          }
          uploadBlockedRef.current = true;
          setLastError(code);
          if (fatalCaptureCodes.has(code)) {
            activeRef.current = false; // Stop capture instead of accumulating lost chunks
            setState(code);
          } else if (activeRef.current) {
            setState('buffering');
          }
          break; // keep data for the next retry instead of skipping gaps
        }
      }
    } catch (error) {
      setLastError(error.code || error.message || 'AUDIO_UPLOAD_FAILED');
      uploadBlockedRef.current = true;
      if (activeRef.current) setState('buffering');
    } finally {
      flushingRef.current = false;
    }
  }, [gameId, token]);

  useEffect(() => {
    if (!conductor || !gameId || !token) return undefined;
    let cancelled = false;
    ensureQueue().then(() => {
      if (!cancelled) void flush();
    }).catch((error) => {
      if (!cancelled) setLastError(error.code || error.message);
    });
    const retry = setInterval(() => { if (!cancelled) void flush(); }, 5000);
    const onOnline = () => { void flush(); };
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      clearInterval(retry);
      window.removeEventListener('online', onOnline);
    };
  }, [conductor, gameId, token, ensureQueue, flush]);

  useEffect(() => {
    if (!conductor || !connected || !recording?.id || recording.status !== 'recording' || !token) {
      activeRef.current = false;
      setState('idle');
      return undefined;
    }
    let cancelled = false;
    const recordingId = recording.id;
    activeRef.current = false;
    setState('preparing');
    const init = async () => {
      await ensureQueue();
      if (cancelled) return;
      let captureId;
      try { captureId = sessionStorage.getItem(captureKey(recordingId)) || ''; } catch {}
      if (!/^[a-f0-9-]{36}$/.test(captureId)) {
        captureId = crypto.randomUUID();
        try { sessionStorage.setItem(captureKey(recordingId), captureId); } catch {}
      }
      const t0 = Date.now();
      const claim = await postJson('claim', { recordingId, captureId });
      if (cancelled) return;
      const t1 = Date.now();
      const clockOffsetMs = claim.serverNow - Math.round((t0 + t1) / 2);
      const startEpochMs = new Date(claim.recordingStartedAt).getTime()
        + (Number(claim.timelineOffsetMs) || 0);
      if (!Number.isFinite(startEpochMs)) throw new Error('INVALID_AUDIO_START_CLOCK');
      captureRef.current = { recordingId, captureId, clockOffsetMs, startEpochMs };
      activeRef.current = true;
      setState('ready');
      void flush();
    };
    init().catch((error) => {
      if (!cancelled) {
        setLastError(error.message || 'AUDIO_SETUP_FAILED');
        setState(error.message || 'AUDIO_SETUP_FAILED');
      }
    });

    const heartbeat = setInterval(async () => {
      const capture = captureRef.current;
      if (renewingRef.current || cancelled || !activeRef.current
        || capture?.recordingId !== recordingId) return;
      renewingRef.current = true;
      try {
        try {
          await postJson('heartbeat', { recordingId, captureId: capture.captureId });
        } catch (error) {
          // If a disconnection lasted longer than the lease, reclaim it
          // using the original capture ID; competing tabs are rejected.
          await postJson('claim', { recordingId, captureId: capture.captureId });
        }
        if (!cancelled) {
          if (!uploadBlockedRef.current) {
            setState('ready');
            setLastError('');
          }
          void flush();
        }
      } catch (error) {
        if (!cancelled) {
          const code = error.message || 'AUDIO_HEARTBEAT_FAILED';
          setLastError(code);
          if (fatalCaptureCodes.has(code)) {
            activeRef.current = false;
            setState(code);
          } else {
            setState('buffering');
          }
        }
      } finally {
        renewingRef.current = false;
      }
    }, 12000);
    return () => {
      cancelled = true;
      activeRef.current = false;
      clearInterval(heartbeat);
    };
  }, [conductor, connected, recording?.id, recording?.status, token, ensureQueue, postJson, flush]);

  const onSegment = useCallback(async ({
    recordingId, blob, startWallTime, endWallTime, mimeType, sourceIdentities,
  }) => {
    const capture = captureRef.current;
    if (!capture || capture.recordingId !== recordingId) {
      throw new Error('AUDIO_CAPTURE_SESSION_UNAVAILABLE');
    }
    if (!Array.isArray(sourceIdentities) || sourceIdentities.length === 0) {
      return; // no consented voice was connected; preserve a silent gap in replay
    }
    const db = await ensureQueue();
    const startMs = Math.max(0, Math.round(startWallTime + capture.clockOffsetMs
      - capture.startEpochMs));
    const durationMs = Math.max(1, Math.round(endWallTime - startWallTime));
    try {
      await enqueueAudio(db, {
        uploadId: crypto.randomUUID(),
        recordingId, gameId, captureId: capture.captureId,
        startMs, durationMs, blob, mimeType,
        sourceIdentities,
      });
      void flush();
    } catch (error) {
      const code = error.code || error.message || 'AUDIO_QUEUE_FAILED';
      setLastError(code);
      if (fatalCaptureCodes.has(code)) {
        activeRef.current = false;
        setState(code);
      }
      throw error;
    }
  }, [ensureQueue, flush, gameId]);

  return {
    state,
    captureReady: (state === 'ready' || state === 'buffering') && activeRef.current,
    onSegment,
    pendingError: lastError,
  };
}
